import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { Wishlist } from '@theme/wishlist';

const SECTION_ID = 'section-rendering-wishlist-item';

/**
 * The wishlist drawer - mirrors assets/cart-drawer.js's DialogComponent usage, plus renders
 * its own row list (fetched per-product via the Section Rendering API) and handles the
 * per-row "add to cart" / remove actions.
 *
 * @typedef {{ dialog: HTMLDialogElement, wishlistItems: HTMLElement, wishlistEmpty: HTMLElement }} WishlistDrawerRefs
 * @extends {DialogComponent<WishlistDrawerRefs>}
 */
class WishlistDrawerComponent extends DialogComponent {
  requiredRefs = ['dialog', 'wishlistItems', 'wishlistEmpty'];

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    document.addEventListener('WishlistUpdateEvent', this.#handleWishlistUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('WishlistUpdateEvent', this.#handleWishlistUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    // Same reason as WishlistCount.updatedCallback() in assets/wishlist.js - this drawer lives
    // inside the header, which sections/header.liquid re-fetches and morphs shortly after every
    // page load. Re-sync so the drawer is never left showing the server's default (empty) state
    // even before it's opened.
    this.#render();
  }

  open() {
    // Re-render every time the drawer is actually opened, not just at connect/update time -
    // this makes it self-correcting regardless of page-load timing, so it can never be stuck
    // showing stale/incomplete content the moment the user looks at it.
    this.#render();
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  #handleWishlistUpdate = () => {
    this.#render();
  };

  async #render() {
    const items = Wishlist.getProducts();

    this.refs.wishlistEmpty.hidden = items.length > 0;

    if (items.length === 0) {
      this.refs.wishlistItems.replaceChildren();
      return;
    }

    const rows = await Promise.all(items.map((item) => this.#fetchRow(item)));
    this.refs.wishlistItems.replaceChildren(...rows.filter((row) => row !== null));
  }

  /**
   * @param {{ id: string, handle: string }} item
   * @returns {Promise<Element | null>}
   */
  async #fetchRow({ handle }) {
    if (!handle) return null;

    try {
      const url = new URL(`/products/${handle}`, window.location.origin);
      const html = await sectionRenderer.getSectionHTML(SECTION_ID, false, url);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const rendered = doc.getElementById(`shopify-section-${SECTION_ID}`);
      return rendered?.querySelector('.wishlist-drawer__row') ?? null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /** @param {Event} event */
  async addToCart(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest('[data-variant-id]');
    if (!(button instanceof HTMLButtonElement)) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;

    try {
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (!addResponse.ok) throw new Error(`Failed to add variant ${variantId} to cart`);

      // Fetch the authoritative cart total rather than assuming a delta, so the header
      // cart bubble stays correct even if the cart already had items in another tab.
      const cart = await fetch('/cart.js').then((response) => response.json());

      document.dispatchEvent(new CartAddEvent({}, 'wishlist-drawer', { itemCount: cart.item_count }));

      this.close();
    } catch (error) {
      console.error(error);
    } finally {
      button.disabled = false;
    }
  }

  /** @param {Event} event */
  removeItem(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const control = target.closest('[data-product-id]');
    const productId = control?.getAttribute('data-product-id');

    if (productId) Wishlist.remove(productId);
  }
}

if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}
